import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { TextInput } from './input/textInput';
import { SearchList } from './searchList';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import useSearch from 'hooks/useSearch';
import { ui } from 'util/ui';
import useDefaultFieldsListener from 'hooks/useDefaultFieldsListener';
import { isShown } from 'views/admin/types';

const filterField: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };
const filterLabel: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#2E333B' };
const filterInput: React.CSSProperties = {
  height: 38,
  boxSizing: 'border-box',
  border: '1px solid #DADDE2',
  borderRadius: 10,
  padding: '0 10px',
  fontFamily: 'inherit',
  fontSize: 14,
  color: '#16181D',
  background: '#FFFFFF',
};

const Container = styled.div`
  width: 100%;
`;

// UI refresh: a white search box with the filter toggle, instead of the green bar.
const Bar = styled.div`
  flex-grow: 1;
  height: 48px;
  background-color: #ffffff;
  border: 1px solid #dadde2;
  border-radius: 12px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  color: #5b616e;
  user-select: none;
  padding: 0 4px 0 8px;
`;

const SearchContainer = styled.div`
  position: relative;
  padding-left: ${(p) => p.theme.space[1]}px;
  padding-right: ${(p) => p.theme.space[1]}px;
  flex-grow: 1;
`;

const CloseIconContainer = styled.div`
  position: absolute;
  top: 6px;
  right: 10px;
`;

const SearchError = styled.div`
  position: absolute;
  top: 45px;
  left: 0;
  width: 100%;
  z-index: 100;
  padding: 12px;
  background-color: ${(p) => p.theme.colors.white};
  color: ${(p) => p.theme.colors.primary};
`;

type Props = {
  setFilter(
    budgetLow: number,
    budgetHigh: number,
    dateLow: Date,
    dateHigh: Date,
    completeState: 'complete' | 'incomplete' | 'all',
  ): void;
  /** Enter: every matching project for the page grid, and the words searched ('' clears the filter). */
  setSearchedProjects(items: IProject[], query: string): void;
};

const ProjectFilter: React.FC<Props> = ({ setFilter, setSearchedProjects }) => {
  // Budget can be switched off in Admin › Settings › Project fields; then it isn't offered here either.
  const { defaults } = useDefaultFieldsListener();
  const showBudget = isShown(defaults, 'showBudget');
  const dispatch = useDispatch();
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchItems, setSearchItems] = useState<(IProject | IRecord | IVideo | INote)[]>([]);
  const [completeState, setCompleteState] = useState<'complete' | 'incomplete' | 'all'>('all');
  const [searchInputValue, setSearchInputValue] = useState('')

  const [budgetLow, setBudgetLow] = useState(0);
  const [budgetHigh, setBudgetHigh] = useState(10000000);
  const [dateLow, setDateLow] = useState(new Date(-1));
  const [dateHigh, setDateHigh] = useState(new Date(2049, 11));

  const [filterMenu, toggleFilterMenu] = useState(false);

  const { searchString, searchAll, preload } = useSearch();
  const [searchError, setSearchError] = useState('');
  // The search the page grid is filtered by (after Enter), and how many projects it found.
  const [filtered, setFiltered] = useState<{ query: string; count: number } | null>(null);

  const clearFilter = () => {
    setFiltered(null);
    setSearchedProjects([], '');
  };

  const search = async (input: string) => {
    if (input !== '') {
      try {
        const results = await searchString(input);
        setSearchError('');
        setSearchItems(results);
      } catch (e) {
        setSearchError((e as Error).message || 'Search failed.');
        setSearchItems([]);
      }
    } else {
      setSearchItems([]);
      clearFilter();
    }
  };

  // Enter (Aaron, 5 Nov 2026): fill the page with every matching project, best matches first.
  const setSearchValueAndSearch = async (input: string) => {
    const query = input.trim();
    if (!query) {
      clearFilter();
      return;
    }
    try {
      const results = await searchAll(query);
      setSearchError('');
      setSearchItems([]);
      setFiltered({ query, count: results.length });
      setSearchedProjects(results, query);
    } catch (e) {
      setSearchError((e as Error).message || 'Search failed.');
    }
  };

  const setSearch = (visible: boolean) => {
    if (visible) preload();
    if (!visible) {
      setTimeout(() => {
        setSearchVisible(false);
      }, 300);
    } else {
      setSearchVisible(true);
    }
  };

  const searchClick = async (id: string) => {
    dispatch(push(`/project/${id}`));
  };

  useEffect(() => {
    setFilter(budgetLow, budgetHigh, dateLow, dateHigh, completeState);
  }, [budgetLow, budgetHigh, dateLow, dateHigh, completeState]);

  const resetFilters = () => {
    setCompleteState('all');
    setBudgetLow(0);
    setBudgetHigh(10000000);
    setDateLow(new Date(-1));
    setDateHigh(new Date(2049, 11));
  };

  return (
    <Container>
      <Bar>
        <SearchContainer>
          <TextInput
            value={searchInputValue}
            setInput={setSearchInputValue}
            name={'projectSearch'}
            placeholder={'Search projects, clients, file names, notes…'}
            update={search}
            style={'primary'}
            noBorder
            isActive={setSearch}
            continuousUpdate={true}
            onEnterPress={setSearchValueAndSearch}
          />
          {searchVisible && <SearchList key={'Search List'} items={searchItems as IProject[]} click={searchClick} />}
          {searchVisible && searchError && <SearchError>{searchError}</SearchError>}
          {searchInputValue && (
            <CloseIconContainer>
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => {
                  setSearchInputValue('');
                  setSearchItems([]);
                  clearFilter();
                }}
                style={{ width: 28, height: 28, border: 'none', borderRadius: 8, background: '#F1F2F4', color: '#5B616E', fontSize: 18, lineHeight: 1, cursor: 'pointer' }}
              >
                ×
              </button>
            </CloseIconContainer>
          )}
        </SearchContainer>
        <button
          type="button"
          className="archy-btn"
          aria-expanded={filterMenu}
          onClick={() => toggleFilterMenu(!filterMenu)}
          style={{
            height: 36,
            padding: '0 12px',
            borderRadius: 10,
            border: `1px solid ${filterMenu ? '#16181D' : '#DADDE2'}`,
            background: filterMenu ? '#16181D' : '#FFFFFF',
            color: filterMenu ? '#FFFFFF' : '#2E333B',
            font: 'inherit',
            fontSize: 13,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            whiteSpace: 'nowrap',
          }}
        >
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 6h16M7 12h10M10 18h4" />
          </svg>
          {/* On a phone the button shrinks to "Filters" so the search box keeps its room. */}
          <span className="archy-hide-phone">{showBudget ? 'Status, date and budget' : 'Status and date'}</span>
          <span className="archy-show-phone">Filters</span>
        </button>
      </Bar>
      {filtered && (
        <div
          role="status"
          style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 10, fontSize: 14, color: '#2E333B' }}
        >
          <span>
            {filtered.count === 0 ? (
              <>No projects match "<strong>{filtered.query}</strong>".</>
            ) : (
              <>
                Showing <strong>{filtered.count.toLocaleString()}</strong> project{filtered.count === 1 ? '' : 's'} matching "
                <strong>{filtered.query}</strong>", best matches first.
              </>
            )}
          </span>
          <button
            type="button"
            className="archy-btn"
            onClick={() => {
              setSearchInputValue('');
              setSearchItems([]);
              clearFilter();
            }}
            style={{ height: 32, padding: '0 12px', borderRadius: 8, border: '1px solid #DADDE2', background: '#FFFFFF', color: '#16181D', font: 'inherit', fontSize: 13, cursor: 'pointer' }}
          >
            Clear search
          </button>
        </div>
      )}
      {filterMenu && (
        // UI refresh (Aaron, 5 Nov 2026): one rounded panel with plain controls. Blank dates and budgets
        // mean "any"; the filtering itself is unchanged.
        <div
          style={{
            marginTop: 10,
            background: '#FFFFFF',
            border: '1px solid #E4E6EA',
            borderRadius: 14,
            padding: '14px 16px',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: '14px 22px',
            fontFamily: ui.font,
          }}
        >
          <div style={filterField}>
            <span style={filterLabel}>Status</span>
            <div role="group" aria-label="Project status" style={{ display: 'inline-grid', gridTemplateColumns: 'repeat(3, auto)', gap: 4, padding: 4, background: ui.subtle, borderRadius: 10 }}>
              {(['all', 'complete', 'incomplete'] as const).map((s) => {
                const on = completeState === s;
                return (
                  <button
                    key={s}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setCompleteState(s)}
                    style={{
                      height: 30,
                      padding: '0 12px',
                      border: 'none',
                      borderRadius: 8,
                      background: on ? ui.surface : 'transparent',
                      boxShadow: on ? '0 1px 3px rgba(0,0,0,0.14)' : 'none',
                      color: on ? ui.ink : ui.muted,
                      fontFamily: 'inherit',
                      fontSize: 13,
                      fontWeight: on ? 600 : 500,
                      cursor: 'pointer',
                    }}
                  >
                    {s === 'all' ? 'All' : s === 'complete' ? 'Complete' : 'Incomplete'}
                  </button>
                );
              })}
            </div>
          </div>
          <label style={filterField}>
            <span style={filterLabel}>From</span>
            <input
              type="date"
              value={dateLow.getFullYear() < 1990 ? '' : moment(dateLow).format('YYYY-MM-DD')}
              onChange={(e) => setDateLow(e.target.value ? moment(e.target.value, 'YYYY-MM-DD').toDate() : new Date(-1))}
              style={{ ...filterInput, width: 150 }}
            />
          </label>
          <label style={filterField}>
            <span style={filterLabel}>To</span>
            <input
              type="date"
              value={dateHigh.getFullYear() >= 2049 ? '' : moment(dateHigh).format('YYYY-MM-DD')}
              onChange={(e) => setDateHigh(e.target.value ? moment(e.target.value, 'YYYY-MM-DD').endOf('day').toDate() : new Date(2049, 11))}
              style={{ ...filterInput, width: 150 }}
            />
          </label>
          {showBudget && (
          <>
          <label style={filterField}>
            <span style={filterLabel}>Budget from ($)</span>
            <input
              type="number"
              min={0}
              placeholder="Any"
              value={budgetLow > 0 ? budgetLow : ''}
              onChange={(e) => setBudgetLow(e.target.value ? Math.max(0, Number(e.target.value)) : 0)}
              style={{ ...filterInput, width: 120 }}
            />
          </label>
          <label style={filterField}>
            <span style={filterLabel}>Budget to ($)</span>
            <input
              type="number"
              min={0}
              placeholder="Any"
              value={budgetHigh < 10000000 ? budgetHigh : ''}
              onChange={(e) => setBudgetHigh(e.target.value ? Math.max(0, Number(e.target.value)) : 10000000)}
              style={{ ...filterInput, width: 120 }}
            />
          </label>
          </>
          )}
          <button
            type="button"
            className="archy-btn"
            onClick={resetFilters}
            style={{ height: 38, padding: '0 14px', borderRadius: 10, border: `1px solid ${ui.control}`, background: ui.surface, color: ui.ink, fontFamily: 'inherit', fontSize: 13, cursor: 'pointer' }}
          >
            Reset
          </button>
        </div>
      )}
    </Container>
  );
};

export default ProjectFilter;
